import { XIcon } from '@phosphor-icons/react';

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/ui/ContextMenu';
import classNames from '@/utils/classnames';

import { useSelectionStore } from '../../../stores/selection-store';

import { getFileIcon } from './file-icons';

interface FileTabStripProps {
  /** Sandbox filesystem root (e.g. `/home/zooid/workspace`), for "Copy Relative Path" — `paths`
   * themselves are already absolute, the same as everywhere else in the file tree/viewer. */
  workspaceRoot: string | null;
  paths: string[];
  activePath: string | null;
  onSelect: (path: string) => void;
  onClose: (path: string) => void;
  onCloseOthers: (path: string) => void;
  onCloseToRight: (path: string) => void;
  onCloseAll: () => void;
  onPreview: (path: string) => void;
}

/** `path` relative to `root`, when it actually sits under it — otherwise `path` unchanged. */
function relativeTo(path: string, root: string | null): string {
  if (!root) return path;
  const prefix = root.endsWith('/') ? root : `${root}/`;
  return path.startsWith(prefix) ? path.slice(prefix.length) : path;
}

export function FileTabStrip({
  workspaceRoot,
  paths,
  activePath,
  onSelect,
  onClose,
  onCloseOthers,
  onCloseToRight,
  onCloseAll,
  onPreview,
}: FileTabStripProps) {
  const addFile = useSelectionStore((s) => s.addFile);

  const attachFile = (path: string) => addFile({ path });

  return (
    <div className="flex h-8.5 shrink-0 items-stretch overflow-x-auto border-b border-line-subtle bg-surface-1">
      {paths.map((path, index) => {
        const FileTypeIcon = getFileIcon(path.split('/').pop() ?? path);
        const isMarkdown = path.toLowerCase().endsWith('.md');
        return (
          <ContextMenu key={path}>
            <ContextMenuTrigger asChild>
              <div
                className={classNames(
                  'group flex shrink-0 cursor-pointer items-center gap-1.5 border-r border-line-subtle px-2.5 font-mono text-2xs',
                  activePath === path ? 'bg-surface-2 text-fg' : 'text-fg-secondary hover:bg-surface-2/60',
                )}
                onClick={() => onSelect(path)}
              >
                <FileTypeIcon size={13} className="shrink-0 text-fg-tertiary" />
                <span className="max-w-48 truncate">{path.split('/').pop()}</span>
                <button
                  type="button"
                  aria-label={`Close ${path}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(path);
                  }}
                  className="rounded p-0.5 text-fg-tertiary opacity-0 hover:bg-surface-3 hover:text-fg group-hover:opacity-100"
                >
                  <XIcon size={11} />
                </button>
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem onSelect={() => onClose(path)}>Close</ContextMenuItem>
              <ContextMenuItem disabled={paths.length < 2} onSelect={() => onCloseOthers(path)}>
                Close Others
              </ContextMenuItem>
              <ContextMenuItem disabled={index === paths.length - 1} onSelect={() => onCloseToRight(path)}>
                Close to the Right
              </ContextMenuItem>
              <ContextMenuItem onSelect={onCloseAll}>Close All</ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={() => attachFile(path)}>Add to chat</ContextMenuItem>
              {isMarkdown && <ContextMenuItem onSelect={() => onPreview(path)}>Preview</ContextMenuItem>}
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={() => navigator.clipboard.writeText(path)}>Copy Path</ContextMenuItem>
              <ContextMenuItem onSelect={() => navigator.clipboard.writeText(relativeTo(path, workspaceRoot))}>
                Copy Relative Path
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        );
      })}
    </div>
  );
}
